'use client';

import type { HubSelectOption } from 'src/components/cmui/hub/hub-select';

import { useResponsive } from 'src/hooks/use-responsive';

import { channelLabel, countryLabel } from 'src/utils/platform/channel-label';

import { HubPeriodSelect } from 'src/components/cmui/hub/hub-period-select';
import { HubFilterGroups } from 'src/components/cmui/hub/hub-filter-groups';
import { FilterResetButton } from 'src/components/cmui/filters/filter-reset-button';

import { brandHubFilterGroups } from './brand-hub-filter-groups';

/**
 * 상세 머리의 **조건 고르개** — 목록에서 고른 것을 여기서도 바꾼다.
 *
 * 🚨 **여기서도 바꿀 수 있어야 한다.** 밖에서 브랜드 하나를 집어 들어오면 목록을 거치지 않으므로,
 *   상세가 읽기만 하면 걸린 조건을 풀 길이 아예 없다.
 * 🔑 값의 주인은 그대로 화면(`brand-hub-view`)이다 — 여기는 고르개만 그린다. 목록과 상세가
 *   **같은 상태**를 보므로 뒤로 나가도 조건이 이어진다.
 * ⚠️ **안 걸린 축도 보인다**(꺼낸 요약 칩 · 「필터」 판의 섹션) — 축이 빠지면 그 조건이 있는 줄도 모르고, 「왜 숫자가 작지?」가 된다.
 * ⚠️ 잠긴 기간은 **자리를 남기고 자물쇠를 단다**(플랜 · `HubPeriodSelect`) — 고를 때만 업그레이드를 안내하고 고른 기간은 그대로다.
 * 🔑 축은 목록과 **한 벌**이다(`brandHubFilterGroups`) — 넓으면 제품군을 요약 칩으로 꺼내 두고 나머지는 「필터」 판 · 폰 폭(md 아래)은 제품군도 판 안이다(목록과 같다).
 * 🔑 칸들을 **낱낱으로** 돌려준다(묶음 `Stack` 없이) — 줄 모양은 상세 머리(`HubDetailToolbar`)의 칩 줄이 쥔다.
 */
/** 조건 고르개의 값과 손짓 — 상세 머리(`BrandHubDetailToolbar`)가 그대로 넘긴다 */
export type BrandHubConditionBarProps = {
  days: number;
  dayOptions: readonly number[];
  /** 플랜이 막는 기간인가 — 막힌 칸은 자리를 남기고 자물쇠를 단다 */
  isPeriodLocked: (days: number) => boolean;
  /** 잠긴 칸을 눌렀을 때 업그레이드를 안내한다 — 안내했으면 `true` */
  guardPeriod: (days: number) => boolean;
  onChangeDays: (days: number) => void;
  /** 달력으로 직접 고른 기간의 이름 — 있으면 프리셋 대신 이것을 적는다 */
  rangeLabel: string;
  mainCode: string;
  mainOptions: HubSelectOption[];
  subCode: string;
  subOptions: HubSelectOption[];
  onPickCategory: (mainCode: string, subCode: string) => void;
  channels: string[];
  channelOptions: readonly string[];
  onToggleChannel: (channel: string) => void;
  countries: string[];
  countryOptions: readonly string[];
  onToggleCountry: (code: string) => void;
  /** 고른 페르소나 토큰 — 비면 전체 */
  personas: string[];
  onTogglePersona: (token: string) => void;
};

export function BrandHubConditionBar({
  days,
  dayOptions,
  isPeriodLocked,
  guardPeriod,
  onChangeDays,
  rangeLabel,
  mainCode,
  mainOptions,
  subCode,
  subOptions,
  onPickCategory,
  channels,
  channelOptions,
  onToggleChannel,
  countries,
  countryOptions,
  onToggleCountry,
  personas,
  onTogglePersona,
}: BrandHubConditionBarProps) {
  const isNarrow = useResponsive('down', 'md');
  const groups = brandHubFilterGroups({
    mainCode,
    mainOptions,
    subCode,
    subOptions,
    onPickCategory,
    channels,
    channelOptions: channelOptions.map((channel) => ({ value: channel, label: channelLabel(channel) })),
    onToggleChannel,
    countries,
    countryOptions: countryOptions.map((code) => ({ value: code, label: countryLabel(code) })),
    onToggleCountry,
    personas,
    onTogglePersona,
    pinMain: !isNarrow,
  });
  const narrowed = groups.filter((group) => group.active).length;
  /** 걸린 축을 모두 「전체」로 — 기간은 늘 걸려 있어 그대로 둔다 */
  const reset = () => groups.forEach((group) => group.active && group.onClear?.());

  return (
    <>
      <HubPeriodSelect
        days={days}
        dayOptions={dayOptions}
        rangeLabel={rangeLabel}
        isPeriodLocked={isPeriodLocked}
        guardPeriod={guardPeriod}
        onChangeDays={onChangeDays}
      />

      <HubFilterGroups groups={groups} narrowed={narrowed} onReset={reset} />
      <FilterResetButton show={narrowed > 0} onClick={reset} />
    </>
  );
}
